import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ayarAPI, muvekkilYonetimAPI } from '../../api/api';
import { waNumara, waBaglanti, sablonDoldur, dosyaDegerleri, bosAlanlar, kodIstiyor } from '../../utils/whatsapp';
import './WhatsAppGonder.css';
import { onayla } from '../../utils/onayla';

/**
 * Dosya detayında "WhatsApp'tan bilgi ver" düğmesi ve şablon penceresi.
 *
 * Sistem mesajı kendisi GÖNDERMEZ. Şablonu dosyanın bilgileriyle doldurup
 * WhatsApp'ı hazır mesajla açar; gönder tuşuna kullanıcı basar. Bunun
 * nedeni WhatsApp'ın programatik gönderim için (Cloud API) ayrı bir
 * numara, Meta işletme doğrulaması ve şablon onayı istemesi — ve o
 * numaranın normal WhatsApp uygulamasında kullanılamaz hale gelmesi.
 *
 * Düğme üç durumda gizlenir/pasifleşir:
 *   - Ayarlarda büro numarası tanımlı değilse hiç görünmez
 *   - Müvekkilin telefonu kayıtlı değilse pasif, sebebi yazar
 *   - Şablon yoksa pencere boş açılmaz
 */
const WhatsAppGonder = ({ dosya }) => {
  const [ayar, setAyar] = useState(null);
  const [acik, setAcik] = useState(false);
  const [secili, setSecili] = useState(0);
  const [metin, setMetin] = useState('');
  /* Giriş kodu durumu. Kod GÖNDERİRKEN üretilir, şablon seçilince
     değil — sırf metne bakmak müvekkilin şifresini sıfırlamamalı. */
  const [girisDurumu, setGirisDurumu] = useState(null);
  const [uretiliyor, setUretiliyor] = useState(false);
  const [kodHata, setKodHata] = useState('');

  useEffect(() => {
    let iptal = false;
    (async () => {
      try {
        const v = await ayarAPI.whatsappGetir();
        if (!iptal) setAyar(v);
      } catch {
        // Ayar okunamazsa özellik sessizce kapalı kalır; dosya ekranı
        // bir ayar hatası yüzünden bozulmamalı.
        if (!iptal) setAyar(null);
      }
    })();
    return () => { iptal = true; };
  }, []);

  const sablonlar = (ayar?.sablonlar ?? ayar?.Sablonlar ?? []).map((s) => ({
    ad: s.ad ?? s.Ad ?? '',
    metin: s.metin ?? s.Metin ?? '',
  }));

  const buroNumara = ayar?.numara ?? ayar?.Numara ?? '';
  const muvekkilTc = dosya?.MuvekkilTC ?? dosya?.muvekkilTC
                  ?? dosya?.MuvekkilTc ?? dosya?.muvekkilTc ?? '';
  const degerler = dosyaDegerleri(dosya);
  const muvekkilTel = dosya?.MuvekkilTelefon ?? dosya?.muvekkilTelefon;
  const hedefNumara = waNumara(muvekkilTel);

  /* Giriş kodu isteyen bir şablon seçilince müvekkilin durumu okunur.
     Okuma kod ÜRETMEZ; yalnızca "kendi şifresi var mı" sorusunu
     yanıtlar, böylece uyarıyı üretimden önce gösterebiliyoruz. */
  useEffect(() => {
    if (!acik || !muvekkilTc) return;
    if (!kodIstiyor(sablonlar[secili]?.metin || '')) return;
    if (girisDurumu?.tc === muvekkilTc) return;

    let iptal = false;
    (async () => {
      try {
        const d = await muvekkilYonetimAPI.girisDurumu(muvekkilTc);
        if (!iptal) setGirisDurumu(d);
      } catch {
        // Durum okunamazsa üretim yine yapılabilir; yalnızca uyarı
        // gösterilemez. Bu yüzden sessiz geçiliyor.
        if (!iptal) setGirisDurumu(null);
      }
    })();
    return () => { iptal = true; };
  }, [acik, secili, muvekkilTc]);

  // Ayar yoksa ya da numara tanımlı değilse düğme hiç çıkmaz.
  if (!buroNumara) return null;

  const pencereyiAc = () => {
    setSecili(0);
    setMetin(sablonDoldur(sablonlar[0]?.metin || '', degerler));
    setKodHata('');
    setAcik(true);
  };

  const sablonSec = (i) => {
    setSecili(i);
    setMetin(sablonDoldur(sablonlar[i].metin, degerler));
    setKodHata('');
  };

  /* Seçili şablonun dosyada karşılığı boş kalan alanları. Düzenlenmiş
     metne değil ŞABLONA bakıyoruz: kullanıcı eksik cümleyi elle silmiş
     olabilir, o zaman uyarı da kalkmalı — ama sildiği yer tutucu metinde
     durduğu sürece uyarı devam etmeli. */
  const eksikAlanlar = sablonlar.length > 0
    ? bosAlanlar(sablonlar[secili]?.metin || '', degerler)
    : [];

  const kodGerekli = kodIstiyor(sablonlar[secili]?.metin || '')
                  && metin.includes('{girisKodu}');

  const ac = (m) => {
    const b = waBaglanti(muvekkilTel, m);
    if (!b) return;
    window.open(b, '_blank', 'noopener,noreferrer');
    setAcik(false);
  };

  const gonder = async () => {
    if (!kodGerekli) { ac(metin); return; }

    /* Kod üretmek müvekkilin mevcut şifresini geçersiz kılar. Şifresi
       varsa uygulamaya giremez hale gelir ve bunu ancak denerken
       anlar — bu yüzden onay isteniyor. Şifresi yoksa soru anlamsız. */
    if (girisDurumu?.sifreBelirlendi && !await onayla(
      'Müvekkil kendi şifresini belirlemiş.\n\n'
      + 'Yeni giriş kodu üretilirse o şifre GEÇERSİZ olur ve uygulamadaki açık '
      + 'oturumu kapanır; müvekkil yeni kodla girip şifresini yeniden belirlemek zorunda kalır.\n\n'
      + 'Devam edilsin mi?')) return;

    setUretiliyor(true);
    setKodHata('');
    try {
      const d = await muvekkilYonetimAPI.girisKoduUret(muvekkilTc);
      const kod = d?.kod ?? d?.Kod ?? '';
      if (!kod) throw new Error('Kod alınamadı.');
      ac(metin.split('{girisKodu}').join(kod));
    } catch (e) {
      setKodHata(e?.response?.data?.message || e?.message || 'Giriş kodu üretilemedi.');
    } finally {
      setUretiliyor(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="wa-btn"
        onClick={pencereyiAc}
        title={hedefNumara
          ? 'Şablon seçip WhatsApp\'ı hazır mesajla açar'
          : 'Müvekkilin telefonu kayıtlı değil'}
      >
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
          <path d="M17.47 14.38c-.3-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.64.08-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.47-1.75-1.64-2.05-.17-.3-.02-.46.13-.6.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.6-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.22 3.08c.15.2 2.1 3.2 5.08 4.49.71.3 1.26.49 1.7.63.71.22 1.36.19 1.87.12.57-.09 1.75-.72 2-1.41.25-.69.25-1.28.17-1.41-.07-.13-.27-.2-.57-.35Z" />
          <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.46 1.32 4.96L2 22l5.25-1.38a9.87 9.87 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm0 18.13h-.01a8.2 8.2 0 0 1-4.18-1.15l-.3-.18-3.11.82.83-3.04-.2-.31a8.17 8.17 0 0 1-1.25-4.36c0-4.53 3.7-8.22 8.23-8.22 2.2 0 4.26.86 5.81 2.41a8.16 8.16 0 0 1 2.41 5.82c0 4.53-3.7 8.21-8.23 8.21Z" />
        </svg>
        <span>WhatsApp'tan bilgi ver</span>
      </button>

      {acik && createPortal(
        <div className="wa-perde" onClick={() => setAcik(false)}>
          <div className="wa-pencere" onClick={(e) => e.stopPropagation()}>
            <header className="wa-basi">
              <h3>WhatsApp'tan bilgi ver</h3>
              <button type="button" className="wa-kapat" onClick={() => setAcik(false)} aria-label="Kapat">×</button>
            </header>

            <div className="wa-hedef">
              <span className="wa-hedef-ad">{degerler.muvekkil || 'Müvekkil'}</span>
              <span className={'wa-hedef-tel' + (hedefNumara ? '' : ' yok')}>
                {hedefNumara ? muvekkilTel : 'Telefon kayıtlı değil'}
              </span>
            </div>

            {sablonlar.length > 0 ? (
              <>
                <div className="wa-sablonlar">
                  {sablonlar.map((s, i) => (
                    <button
                      key={i}
                      type="button"
                      className={'wa-sablon' + (secili === i ? ' aktif' : '')}
                      onClick={() => sablonSec(i)}
                    >
                      {s.ad}
                    </button>
                  ))}
                </div>

                {eksikAlanlar.length > 0 && (
                  <div className="wa-eksik" role="status">
                    <strong>Dosyada bu bilgiler yok:</strong> {eksikAlanlar.join(', ')}.
                    {' '}Mesajda boş kalıyor — göndermeden önce elle yazın ya da
                    {' '}başka bir şablon seçin.
                  </div>
                )}

                {kodGerekli && (
                  <div className={'wa-kod-not' + (girisDurumu?.sifreBelirlendi || girisDurumu?.girisDurduruldu ? ' dikkat' : '')}>
                    {girisDurumu?.girisDurduruldu ? (
                      <>
                        <strong>Bu müvekkilin girişi durdurulmuş.</strong> Kod gönderilse de
                        {' '}uygulamaya giremez. Önce Müvekkiller sayfasından girişini açın.
                      </>
                    ) : girisDurumu?.sifreBelirlendi ? (
                      <>
                        <strong>Müvekkil kendi şifresini belirlemiş.</strong> Göndermek
                        {' '}yeni kod üretir, o şifreyi geçersiz kılar ve uygulamadaki açık
                        {' '}oturumunu kapatır; müvekkil şifresini yeniden belirlemek zorunda kalır.
                      </>
                    ) : (
                      <>
                        <strong>Giriş kodu gönderirken üretilecek</strong> ve mesaja
                        {' '}yazılacak. Kod 7 gün geçerli; müvekkil bu kodla girip kendi
                        {' '}şifresini belirleyecek.
                      </>
                    )}
                  </div>
                )}

                {kodHata && <p className="wa-uyari">{kodHata}</p>}

                <label className="wa-metin-etiket">
                  Mesaj <span>— göndermeden önce düzenleyebilirsiniz</span>
                </label>
                <textarea
                  className="wa-metin"
                  rows={8}
                  value={metin}
                  onChange={(e) => setMetin(e.target.value)}
                />
              </>
            ) : (
              <p className="wa-uyari">
                Tanımlı şablon yok. Ayarlar sayfasından ekleyebilirsiniz.
              </p>
            )}

            {!hedefNumara && (
              <p className="wa-uyari">
                Bu müvekkilin telefonu kayıtlı olmadığı için mesaj gönderilemez.
                Dosyayı düzenleyip numarayı ekleyin.
              </p>
            )}

            <footer className="wa-dugmeler">
              <button type="button" className="btn btn-secondary" onClick={() => setAcik(false)}>
                Vazgeç
              </button>
              <button
                type="button"
                className="btn wa-ac"
                onClick={gonder}
                disabled={!hedefNumara || !metin.trim() || uretiliyor
                          || (kodGerekli && (!muvekkilTc || !!girisDurumu?.girisDurduruldu))}
                title={kodGerekli && !muvekkilTc
                  ? 'Dosyada TC kimlik no yazılı değil; giriş kodu üretilemez.'
                  : kodGerekli && girisDurumu?.girisDurduruldu
                    ? 'Müvekkilin girişi durdurulmuş; önce girişini açın.'
                    : undefined}
              >
                {uretiliyor
                  ? 'Kod üretiliyor…'
                  : kodGerekli
                    ? 'Kodu üret ve WhatsApp\'ta aç'
                    : 'WhatsApp\'ta Aç'}
              </button>
            </footer>

            <p className="wa-not">
              WhatsApp hazır mesajla açılır; <b>gönder tuşuna siz basarsınız</b>.
            </p>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default WhatsAppGonder;
